// TelcoTrack - Input Component
// Reusable input component with validation and consistent styling

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type InputType = 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date';
export type InputSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ]
})
export class InputComponent implements ControlValueAccessor {
  constructor(private readonly cdr: ChangeDetectorRef) {}
  @Input() type: InputType = 'text';
  @Input() size: InputSize = 'md';
  @Input() placeholder = '';
  @Input() label = '';
  @Input() helperText = '';
  @Input() errorMessage = '';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() required = false;
  @Input() autocomplete = '';
  @Input() maxlength?: number;
  @Input() minlength?: number;
  @Input() pattern?: string;
  @Input() leftIcon?: string;
  @Input() rightIcon?: string;

  @Output() inputChange = new EventEmitter<string>();
  @Output() inputFocus = new EventEmitter<Event>();
  @Output() inputBlur = new EventEmitter<Event>();

  // ControlValueAccessor implementation
  value = '';
  private onChange = (value: string) => {};
  private onTouched = () => {};

  /**
   * Handle input change
   */
  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
    this.inputChange.emit(this.value);
  }

  /**
   * Handle input focus
   */
  onInputFocus(event: Event): void {
    this.inputFocus.emit(event);
  }

  /**
   * Handle input blur
   */
  onInputBlur(event: Event): void {
    this.onTouched();
    this.inputBlur.emit(event);
  }

  /**
   * Get input CSS classes
   */
  get inputClasses(): string {
    const classes = ['input'];
    
    // Size
    if (this.size !== 'md') {
      classes.push(`input-${this.size}`);
    }
    
    // Error state
    if (this.errorMessage) {
      classes.push('error');
    }
    
    // Icon padding
    if (this.leftIcon) {
      classes.push('pl-10');
    }
    
    if (this.rightIcon) {
      classes.push('pr-10');
    }
    
    return classes.join(' ');
  }

  /**
   * Get container CSS classes
   */
  get containerClasses(): string {
    const classes = ['input-container'];
    
    if (this.disabled) {
      classes.push('disabled');
    }
    
    return classes.join(' ');
  }

  // ControlValueAccessor methods
  writeValue(value: string | number | null | undefined): void {
    // Numeric 0 is a real value. `value || ''` turned it into an empty field.
    this.value = value == null ? '' : String(value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
